import { useNavigate, useSearchParams } from "react-router";
import { useTranslation } from "react-i18next";

// The claim a registration was opened from rides in the URL, not in history
// state, so a reload, a new tab or a trip to one of its warranties keeps it.
const FROM_CLAIM = "from_claim";
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** ``path`` carrying the claim it was opened from. */
export function withFromClaim(
  path: string,
  claimUuid: string | null | undefined,
): string {
  if (!claimUuid) return path;
  const [base, hash] = path.split("#");
  const join = base.includes("?") ? "&" : "?";
  return `${base}${join}${FROM_CLAIM}=${claimUuid}${hash ? `#${hash}` : ""}`;
}

/** A claim's link to its registration. */
export function registrationPathFromClaim(
  holdingUuid: string,
  claimUuid: string | undefined,
): string {
  return withFromClaim(`/registrations/${holdingUuid}`, claimUuid);
}

/** The claim this registration page was opened from, or null. */
export function useFromClaim(): string | null {
  const [params] = useSearchParams();
  const value = params.get(FROM_CLAIM);
  return value && UUID.test(value) ? value : null;
}

/** Where "back" goes: the claim that opened this registration, else the list. */
export function useRegistrationBack(): { label: string; go: () => void } {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const fromClaim = useFromClaim();
  return fromClaim
    ? {
        label: t("registrations.backToClaim", "Back to claim"),
        go: () => navigate(`/claims/${fromClaim}`),
      }
    : {
        label: t("registrations.backToList", "Back to registrations"),
        go: () => navigate("/registrations"),
      };
}
